import { useState } from 'react';
import { probeDualAudioElements, probeVolumeControl, probeWebAudio, type ProbeResult } from '../lib/audioProbe';
import { platform } from '../lib/platform';

interface Props {
  /** 実際のプレイヤー操作で観測された「他が止められた」回数 */
  observedInterruptions: number;
}

type Results = {
  volume?: ProbeResult;
  dualAudio?: ProbeResult;
  webAudio?: ProbeResult;
};

const verdictLabel: Record<ProbeResult['verdict'], string> = {
  yes: '✅ できる',
  no: '❌ できない',
  inconclusive: '⚠️ 判定不能',
};

/**
 * 実機検証パネル。既定は閉じている。
 *
 * 画面から取り除いた注意書き（この端末で何ができないか）は、必要なときだけ
 * ここで読めるようにしてある。常時表示すると視聴の邪魔になるため。
 */
export function Diagnostics({ observedInterruptions }: Props) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<Results>({});

  const run = async () => {
    setRunning(true);
    try {
      const volume = await probeVolumeControl();
      const dualAudio = await probeDualAudioElements();
      const webAudio = await probeWebAudio();
      setResults({ volume, dualAudio, webAudio });
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="diag">
      <button className="diag-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? '端末の制約と実機検証を閉じる' : '端末の制約と実機検証'}
      </button>
      {!open ? null : (
        <div className="diag-body">
          <h2>この端末での制約</h2>
          <ul className="diag-facts">
            {platform.isIOS ? (
              <li>
                <strong>上部バーの「音声」は 1 つだけ選べます。</strong>
                {'iOS / iPadOS の WebKit が「音を出せる動画は同時に 1 つ」という制限を' +
                  '課しているため、このアプリは単一音声を前提にしています。' +
                  '映像だけ（ミュート）なら本数の制限はありません。'}
              </li>
            ) : (
              <li>この端末では複数の音声を同時に鳴らせます。上部バーの「音声」で複数を選べます。</li>
            )}
            {platform.isIPhoneLike && (
              <li>JS からの音量指定が OS 側で無効化されるため、音量は各プレイヤー内の操作か本体の音量ボタンで調整してください。</li>
            )}
            <li>
              {'ただしこの制限は再生を開始する瞬間にしか評価されないため、ミュートしたまま両方を' +
                '再生しておいて、あとから片方をプレイヤー内のスピーカーボタンでミュート解除すると、' +
                '2 つ同時に鳴ります。順番に依存し、どちらかで再生が始まり直すと片方が止まるので、' +
                '保証された動作ではありません。'}
            </li>
            <li>画面ロックやアプリの切り替えでは、動画の音声は継続しません（WebKit が中断します）。</li>
            <li>
              {'チャットが未ログイン表示になり発言できない場合は、ブラウザがサードパーティ ' +
                'Cookie をブロックしています。チャットは YouTube / Twitch の埋め込みを' +
                'そのまま出しているだけなので、この設定を変えない限りこのアプリ側でできることはありません。'}
            </li>
          </ul>

          <h2>実機で計測する</h2>
          <p className="diag-env">
            iOS/iPadOS={String(platform.isIOS)} / iPhone 相当={String(platform.isIPhoneLike)} / Safari=
            {String(platform.isSafari)} / standalone PWA={String(platform.isStandalone)}
          </p>
          <button onClick={run} disabled={running}>
            {running ? '計測中…' : '計測する（音は鳴りません）'}
          </button>

          <dl className="diag-list">
            <Row
              term="JS からの音量指定 (HTMLMediaElement.volume)"
              result={results.volume}
              note="✕ の場合、YouTube/Twitch の setVolume も同様に効きません。"
            />
            <Row
              term="audio 要素 2 本の同時再生"
              result={results.dualAudio}
              note="これが ○ でも、video 要素 (=YouTube/Twitch プレイヤー) 2 本の同時再生ができることは意味しません。"
            />
            <Row term="AudioContext (Web Audio)" result={results.webAudio} note="ミキシングの前提条件。" />
            <div className="diag-row">
              <dt>実プレイヤーでの音声中断の観測</dt>
              <dd>
                {observedInterruptions === 0
                  ? 'まだ観測されていません（2 つ以上のタイルで音声を切り替えると計測されます）'
                  : `${observedInterruptions} 回：別のタイルの音声を有効にした直後に、再生中のタイルが自動停止しました。この端末は複数音声の同時再生を許可していません。`}
              </dd>
            </div>
          </dl>

          <p className="diag-foot">
            {'公式プレイヤーと公式の埋め込みチャットだけを使用しています。サーバも API キーも持たず、' +
              'ストリーム URL の抽出や音声の分離は行いません。'}
          </p>
        </div>
      )}
    </section>
  );
}

function Row({ term, result, note }: { term: string; result?: ProbeResult; note: string }) {
  return (
    <div className="diag-row">
      <dt>{term}</dt>
      <dd>
        {result ? (
          <>
            <strong>{verdictLabel[result.verdict]}</strong> — {result.detail}
          </>
        ) : (
          '未計測'
        )}
        <span className="diag-note">{note}</span>
      </dd>
    </div>
  );
}
